import ClinicalSearch from '@ashn/network-search-react/ClinicalSearch';
import ClinicalSearchSkeleton from '@ashn/network-search-react/ClinicalSearchSkeletons';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { GetNetworkSearchContext } from '../../data/apiCalls';
import { NETWORK_SEARCH_ROUTES } from '../../scripts/routes';
import { getHeaderText } from './logic';
import { InvalidPlanMessage } from './SearchSections';

const MAX_ADDRESS_LENGTH = 200;
const MAX_SPECIALTY_CODE = 100000;

// Syntactic check only; the plan's contracts scope results server side.
const parseSpecialtyCode = value => {
	const parsed = Number(value);
	return Number.isInteger(parsed) &&
		parsed > 0 &&
		parsed <= MAX_SPECIALTY_CODE
		? parsed
		: null;
};

const Result = () => {
	const { healthplanCode } = useParams();
	const healthplanCodeLowercase = healthplanCode.toLowerCase();
	const [searchParams] = useSearchParams();

	const specialtyCode = parseSpecialtyCode(searchParams.get('specialtyCode'));
	const address = (searchParams.get('address') ?? '').trim();
	const fullName = (searchParams.get('fullName') ?? '').trim();
	const clinicName = (searchParams.get('clinicName') ?? '').trim();
	const npi = (searchParams.get('npi') ?? '').trim();
	const licenseNumber = (searchParams.get('licenseNumber') ?? '').trim();
	const hasValidSearch =
		specialtyCode !== null &&
		!!address &&
		address.length <= MAX_ADDRESS_LENGTH;

	const clientDetails = useQuery(
		['networkSearchContext', healthplanCode],
		() => GetNetworkSearchContext(healthplanCode),
		{
			enabled: !!healthplanCode,
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			retry: false,
		}
	);

	// Stable identity keeps ClinicalSearch from re-geocoding on every render.
	// Optional advanced search fields are only included when present so
	// ClinicalSearch's own defaults apply otherwise.
	const predefinedSearchParameters = useMemo(() => {
		const params = { specialtyCode, address };
		if (fullName) params.fullName = fullName;
		if (clinicName) params.clinicName = clinicName;
		if (npi) params.npi = npi;
		if (licenseNumber) params.licenseNumber = licenseNumber;
		return params;
	}, [specialtyCode, address, fullName, clinicName, npi, licenseNumber]);

	const searchPath = `/${NETWORK_SEARCH_ROUTES.root}/${healthplanCode}`;

	if (!hasValidSearch) {
		return <Navigate to={searchPath} replace />;
	}

	// The BFF returns 404 for an unknown plan code, which puts the query in error.
	const isValidCode = clientDetails.isSuccess && !!clientDetails.data?.data;
	const contracts = clientDetails.data?.data?.contracts || [];
	const apiToken = clientDetails.data?.data?.apiToken;

	if (clientDetails.isLoading) {
		return <ClinicalSearchSkeleton />;
	}

	return (
		<>
			<Typography variant='h4' sx={{ mb: 3 }}>
				{getHeaderText(healthplanCodeLowercase)}
			</Typography>

			{!isValidCode ? (
				<InvalidPlanMessage />
			) : (
				<>
					<ClinicalSearch
						tenantCode='ASHLINK'
						apiToken={apiToken}
						contracts={contracts}
						predefinedSearchParameters={predefinedSearchParameters}
						nominationPrivacyPolicyUrl='/privacy'
					/>
				</>
			)}
		</>
	);
};

export default Result;
